import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Avatar,
  Divider,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  PlayCircle as PlayIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import CrearContenidoModal from '../../components/Repository/FormCreateRepository';
import BulkDocumentUpload from '../../components/Repository/BulkDocumentUpload';
import RepositoryService from '../../services/RepositoryService';
import VideoPlayer from '../../components/common/VideoPlayer/VideoPlayer';
import Swal from 'sweetalert2';

// Estilo personalizado para los botones
const StyledButton = ({ children, ...props }) => (
  <Button
    {...props}
    sx={{
      px: 2.5,
      py: 1.25,
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: 0.5,
      borderRadius: 2,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none'
      },
      ...props.sx
    }}
  >
    {children}
  </Button>
);

export default function RepositorioTable() {
  const [openModal, setOpenModal] = useState(false);
  const [openBulkUpload, setOpenBulkUpload] = useState(false);
  const [openModalType, setOpenModalType] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalType, setModalType] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemType, setEditingItemType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const parseEtiquetas = (labels) => {
    if (!labels) return [];
    if (Array.isArray(labels)) return labels.filter(t => t && typeof t === 'string');
    if (typeof labels === 'string') return labels.split(',').map(t => t.trim()).filter(t => t);
    return [];
  };

  const stripHtml = (html) => {
    if (typeof html !== 'string') return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [videos, docs] = await Promise.all([
        RepositoryService.fetchVideos(),
        RepositoryService.fetchDocuments(),
      ]);

      const resolveMediaUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
      };

      const toMs = (value) => {
        const t = value ? new Date(value).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      };

      const formattedVideos = videos.map((video) => ({
        id: `video-${video.id}`,
        entityId: video.id,
        tipo: 'video',
        categoria: video.category,
        nombre: video.title,
        descripcion: video.description,
        descripcionTexto: stripHtml(video.description),
        estado: video.isValid,
        etiquetas: parseEtiquetas(video.labels),
        responsable: video.uploadedBy?.name || 'Desconocido',
        createdAtMs: toMs(video.createdAt || video.CreatedAt),
        updatedAtMs: toMs(video.updatedAt || video.UpdatedAt),
        creacion: new Date(video.createdAt).toLocaleDateString(),
        modificacion: new Date(video.updatedAt).toLocaleDateString(),
        thumbnailUrl: resolveMediaUrl(video.thumbnailPath || video.ThumbnailPath),
        raw: video,
        deleting: false
      }));

      const formattedDocs = docs.map((doc) => {
        const contentType = (doc.contentType || doc.ContentType || doc.tipo || 'document').toLowerCase();
        const tipo =
          contentType === 'audio' || contentType === 'podcast'
            ? 'audio'
            : contentType === 'link' || contentType === 'enlace'
              ? 'link'
              : 'documento';
        return {
          id: `${tipo}-${doc.id}`,
          entityId: doc.id,
          tipo,
          contentType,
          categoria: doc.category,
          nombre: doc.title,
          descripcion: doc.description,
          descripcionTexto: stripHtml(doc.description),
          estado: doc.isValid,
          etiquetas: parseEtiquetas(doc.labels),
          responsable: doc.uploadedBy?.name || 'Desconocido',
          createdAtMs: toMs(doc.createdAt || doc.CreatedAt),
          updatedAtMs: toMs(doc.updatedAt || doc.UpdatedAt),
          creacion: new Date(doc.createdAt).toLocaleDateString(),
          modificacion: new Date(doc.updatedAt).toLocaleDateString(),
          thumbnailUrl: resolveMediaUrl(doc.thumbnailPath || doc.ThumbnailPath),
          externalUrl: doc.externalUrl || doc.ExternalUrl || null,
          raw: doc,
          deleting: false
        };
      });

      const combined = [...formattedVideos, ...formattedDocs].sort((a, b) => {
        const byDate = (b.createdAtMs || 0) - (a.createdAtMs || 0);
        if (byDate !== 0) return byDate;
        return Number(b.entityId || 0) - Number(a.entityId || 0);
      });
      setRows(combined);
    } catch (error) {
      console.error('Error cargando datos del repositorio:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudieron cargar los datos del repositorio',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = () => {
    setEditingItemId(null);
    setEditingItemType(null);
    setEditingItem(null);
    setOpenModal(true);
  };

  const handleCloseModal = (success = false) => {
    setOpenModal(false);
    setEditingItemId(null);
    setEditingItemType(null);
    setEditingItem(null);
    if (success) {
      fetchData();
    }
  };

  const handleOpenModalType = (type, url) => {
    setModalType(type);
    setModalContent(url);
    setOpenModalType(true);
  };

  const handleCloseModalType = () => {
    setOpenModalType(false);
    setModalContent(null);
    setModalType('');
  };

  const [filters, setFilters] = useState({
    categoria: '',
    estado: '',
    nombre: '',
    descripcion: '',
    responsable: '',
  });

  const handleFilterChange = (field) => (event) => {
    setFilters({ ...filters, [field]: event.target.value });
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      return (
        (filters.categoria ? row.categoria === filters.categoria : true) &&
        (filters.estado ? row.estado === (filters.estado === 'Vigente') : true) &&
        row.nombre?.toLowerCase().includes(filters.nombre.toLowerCase()) &&
        (row.descripcionTexto || row.descripcion || '')
          .toLowerCase()
          .includes(filters.descripcion.toLowerCase()) &&
        row.responsable?.toLowerCase().includes(filters.responsable.toLowerCase())
      );
    });
  }, [filters, rows]);

  const handleEdit = (item) => {
    setEditingItemId(item.entityId ?? item.raw?.id ?? item.id);
    setEditingItemType(item.tipo);
    setEditingItem(item);
    setOpenModal(true);
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#4daba4',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      focusCancel: true
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        // Marcar la fila como en proceso de eliminación
        const entityId = item.entityId ?? item.id;
        setRows(prevRows => prevRows.map(row => 
          row.id === item.id ? { ...row, deleting: true } : row
        ));

        // Llamar al servicio correspondiente
        if (item.tipo === 'video') {
          await RepositoryService.deleteVideo(entityId);
        } else if (item.tipo === 'documento') {
          await RepositoryService.deleteDocument(entityId);
        }

        // Mostrar notificación de éxito
        await Swal.fire({
          title: 'Eliminado',
          text: 'El contenido ha sido eliminado exitosamente.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Actualizar los datos
        await fetchData();
      } catch (error) {
        console.error('Error al eliminar:', error);
        
        // Revertir el estado de eliminación
        setRows(prevRows => prevRows.map(row => 
          row.id === item.id ? { ...row, deleting: false } : row
        ));

        let errorMessage = 'Ocurrió un error al eliminar el contenido.';
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = 'El contenido no fue encontrado (ya pudo haber sido eliminado).';
          } else if (error.response.status === 403) {
            errorMessage = 'No tienes permisos para eliminar este contenido.';
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message;
          }
        }

        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error'
        });
      } finally {
        setSaving(false);
      }
    }
  };

  const columns = [
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 80,
      renderCell: (params) => (
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32,
            bgcolor: params.value === 'video' ? '#4daba4' : '#f44336'
          }}
        >
          {params.value === 'video' ? (
            <PlayIcon sx={{ fontSize: 16 }} />
          ) : (
            <PdfIcon sx={{ fontSize: 16 }} />
          )}
        </Avatar>
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'thumbnailUrl',
      headerName: 'Miniatura',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.tipo !== 'video') {
          return (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        }
        if (!params.value) {
          return (
            <Typography variant="caption" color="error.main">
              Sin miniatura
            </Typography>
          );
        }
        return (
          <Box
            component="img"
            src={params.value}
            alt={params.row.nombre}
            sx={{
              width: 56,
              height: 36,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        );
      },
    },
    { 
      field: 'categoria', 
      headerName: 'Categoría', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          sx={{ 
            bgcolor: '#e0f2f1',
            color: '#00695c',
            fontWeight: 500
          }} 
        />
      )
    },
    { 
      field: 'nombre', 
      headerName: 'Nombre', 
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.value}
        </Typography>
      ) 
    },
    { 
      field: 'descripcionTexto', 
      headerName: 'Descripción', 
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || stripHtml(params.row.descripcion)}
        </Typography>
      )
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 120,
      renderCell: ({ value }) => (
        <Chip 
          label={value ? 'Vigente' : 'No vigente'} 
          size="small"
          sx={{ 
            bgcolor: value ? '#e8f5e9' : '#ffebee',
            color: value ? '#2e7d32' : '#c62828',
            fontWeight: 500
          }}
        />
      ),
    },
    {
      field: 'etiquetas',
      headerName: 'Etiquetas',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.value?.map((tag, index) => (
            <Chip 
              key={index} 
              label={tag} 
              size="small" 
              sx={{ 
                bgcolor: '#e0f7fa',
                color: '#00838f',
                fontWeight: 500
              }} 
            />
          ))}
        </Box>
      ),
      sortable: false,
      filterable: false,
    },
    { 
      field: 'responsable', 
      headerName: 'Responsable', 
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'creacion', 
      headerName: 'Creación', 
      width: 120,
      valueGetter: (_, row) => row?.createdAtMs || 0,
      sortComparator: (a, b) => Number(a || 0) - Number(b || 0),
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.creacion}
        </Typography>
      )
    },
    { 
      field: 'modificacion', 
      headerName: 'Modificación', 
      width: 120,
      valueGetter: (_, row) => row?.updatedAtMs || 0,
      sortComparator: (a, b) => Number(a || 0) - Number(b || 0),
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.modificacion}
        </Typography>
      )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 160,
      renderCell: (params) => {
        const filePath = params.row.raw?.path;
        const fileType = params.row.tipo;
        // REACT_APP_LOCATION apunta a la API sin /api (ej. http://localhost:5116),
        // donde se sirven/redirigen /videos, /documents, etc.
        const fullUrl = filePath
          ? `${process.env.REACT_APP_LOCATION || ''}${filePath.startsWith('/') ? filePath : `/${filePath}`}`
          : null;
    
        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Ver">
              <IconButton
                size="small"
                disabled={!fullUrl || params.row.deleting}
                onClick={() => {
                  if (!fullUrl) return;
                  if (fileType === 'video') {
                    handleOpenModalType('video', fullUrl);
                  } else if (fileType === 'documento') {
                    handleOpenModalType('pdf', fullUrl);
                  } else {
                    window.open(fullUrl, '_blank');
                  }
                }}
                sx={{ 
                  color: (!fullUrl || params.row.deleting) ? 'action.disabled' : '#4daba4',
                  '&:hover': { bgcolor: 'rgba(77, 171, 164, 0.08)' }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Editar">
              <IconButton 
                size="small" 
                onClick={() => !params.row.deleting && handleEdit(params.row)}
                disabled={saving || params.row.deleting}
                sx={{ 
                  color: (saving || params.row.deleting) ? 'action.disabled' : '#4daba4',
                  '&:hover': { bgcolor: 'rgba(77, 171, 164, 0.08)' }
                }}
              >
                {saving && params.row.entityId === editingItemId && params.row.tipo === editingItemType ? (
                  <CircularProgress size={20} sx={{ color: '#4daba4' }} />
                ) : (
                  <EditIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Eliminar">
              <IconButton 
                size="small" 
                onClick={() => !params.row.deleting && handleDelete(params.row)}
                disabled={saving || params.row.deleting}
                sx={{ 
                  color: (saving || params.row.deleting) ? 'action.disabled' : '#f44336',
                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' }
                }}
              >
                {params.row.deleting ? (
                  <CircularProgress size={20} sx={{ color: '#f44336' }} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Copiar enlace">
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(fullUrl || '');
                  Swal.fire({
                    title: 'Enlace copiado',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                  });
                }}
                disabled={!fullUrl || saving || params.row.deleting}
                sx={{ 
                  color: (!fullUrl || saving || params.row.deleting) ? 'action.disabled' : '#4daba4',
                  '&:hover': { bgcolor: 'rgba(77, 171, 164, 0.08)' }
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
      sortable: false,
      filterable: false,
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600} color="#4daba4">
            Repositorio de Contenidos
          </Typography>
          
          <Box display="flex" gap={2}>
            <StyledButton
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              sx={{
                bgcolor: 'rgba(77, 171, 164, 0.1)',
                color: '#4daba4',
                '&:hover': {
                  bgcolor: 'rgba(77, 171, 164, 0.2)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(77, 171, 164, 0.05)',
                  color: 'rgba(77, 171, 164, 0.5)'
                }
              }}
            >
              {loading ? 'Cargando...' : 'Actualizar'}
            </StyledButton>
            
            <StyledButton
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setOpenBulkUpload(true)}
              disabled={loading}
              sx={{
                bgcolor: 'rgba(77, 171, 164, 0.12)',
                color: '#4daba4',
                '&:hover': {
                  bgcolor: 'rgba(77, 171, 164, 0.22)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(77, 171, 164, 0.05)',
                  color: 'rgba(77, 171, 164, 0.5)'
                }
              }}
            >
              Carga masiva
            </StyledButton>

            <StyledButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              disabled={loading}
              sx={{ 
                bgcolor: '#4daba4',
                '&:hover': {
                  bgcolor: '#3d9993'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(77, 171, 164, 0.5)'
                }
              }}
            >
              Nuevo Contenido
            </StyledButton>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1} color="text.secondary">
            <FilterIcon fontSize="small" />
            <Typography variant="subtitle2">Filtrar por:</Typography>
          </Box>
          
          <Select
            value={filters.categoria}
            onChange={handleFilterChange('categoria')}
            displayEmpty
            size="small"
            disabled={loading}
            sx={{ 
              minWidth: 180,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4daba4'
              }
            }}
          >
            <MenuItem value="">Todas las categorías</MenuItem>
            <MenuItem value="Monografía">Monografía</MenuItem>
            <MenuItem value="Ayuda Visual">Ayuda Visual</MenuItem>
            <MenuItem value="Guía de Manejo">Guía de Manejo</MenuItem>
          </Select>

          <Select
            value={filters.estado}
            onChange={handleFilterChange('estado')}
            displayEmpty
            size="small"
            disabled={loading}
            sx={{ 
              minWidth: 160,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4daba4'
              }
            }}
          >
            <MenuItem value="">Todos los estados</MenuItem>
            <MenuItem value="Vigente">Vigente</MenuItem>
            <MenuItem value="No vigente">No vigente</MenuItem>
          </Select>

          <TextField
            size="small"
            label="Nombre"
            value={filters.nombre}
            onChange={handleFilterChange('nombre')}
            disabled={loading}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#4daba4'
                }
              }
            }}
          />

          <TextField
            size="small"
            label="Descripción"
            value={filters.descripcion}
            onChange={handleFilterChange('descripcion')}
            disabled={loading}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#4daba4'
                }
              }
            }}
          />

          <TextField
            size="small"
            label="Responsable"
            value={filters.responsable}
            onChange={handleFilterChange('responsable')}
            disabled={loading}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#4daba4'
                }
              }
            }}
          />
        </Box>

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
              sorting: {
                sortModel: [{ field: 'creacion', sort: 'desc' }],
              },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#f5f5f5',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'rgba(77, 171, 164, 0.04)',
              }
            }}
          />
        </Box>
      </Paper>

      <CrearContenidoModal
        key={editingItemId ? `edit-${editingItemType}-${editingItemId}` : 'create'}
        open={openModal}
        onClose={handleCloseModal}
        editingItemId={editingItemId}
        editingItemType={editingItemType}
        editingItem={editingItem}
      />

      <BulkDocumentUpload
        open={openBulkUpload}
        onClose={() => setOpenBulkUpload(false)}
        onCompleted={(result) => {
          const uploaded = result?.uploaded ?? result?.Uploaded ?? 0;
          if (uploaded > 0) {
            fetchData();
          }
        }}
      />

      <Dialog 
        open={openModalType} 
        onClose={handleCloseModalType} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#4daba4', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Vista Previa
          <IconButton 
            onClick={handleCloseModalType} 
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
          {modalType === 'pdf' && (
            <iframe
              src={modalContent}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="PDF Viewer"
            />
          )}
          {modalType === 'video' && (
            <VideoPlayer url={modalContent} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}